import {
    ComponentKind,
    isStringTypeKind,
    ActionKind,
    LegacyPropertyDescription,
    PropertyKind,
    PropertyDescription,
    InputOutputTables,
    MutatingScreenKind,
    TableGlideType,
    ActionComponentDescription,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import {
    ComponentBuilder,
    ComponentDescriptor,
    EnumPropertyCase,
    ComponentConfiguratorContext,
    ActionPropertyDescriptor,
} from ".";
import { defined, definedMapWithDefault } from "../../Support";
import { ComponentHandlerBase } from "./handler";
import { getEnumProperty } from "./property-utils";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";
import { makeTextPropertyDescriptor } from "./descriptor-utils";
import { makeText } from "../builder-utils";
import { makeDefaultActionDescriptorWithKinds } from "./primitive";

const ComponentKindMap: ComponentKind = "map";

enum MapLocationKind {
    Address = "address",
}

enum MapZoomLevel {
    Near = "near",
    Medium = "medium",
    Far = "far",
}

const mapZoomLevels: ReadonlyArray<EnumPropertyCase<MapZoomLevel> & { level: number }> = [
    {
        value: MapZoomLevel.Near,
        label: "Near",
        level: 15,
    },
    {
        value: MapZoomLevel.Medium,
        label: "Medium",
        level: 12,
    },
    {
        value: MapZoomLevel.Far,
        label: "Far",
        level: 9,
    },
];

function getZoomLevelNumber(zoomLevel: MapZoomLevel | undefined): number {
    const entry = mapZoomLevels.find(l => l.value === zoomLevel);
    return definedMapWithDefault(entry, mapZoomLevels[0].level, e => e.level);
}

interface MapLocationAddress {
    readonly kind: MapLocationKind.Address;

    readonly addressPropertyName: LegacyPropertyDescription;
}

type MapLocation = MapLocationAddress;

interface MapComponentDescription extends ActionComponentDescription {
    readonly markerPropertyName: LegacyPropertyDescription;

    readonly location: MapLocation;
    readonly zoomLevel: PropertyDescription | undefined;
}

export class MapComponentHandler extends ComponentHandlerBase<MapComponentDescription> {
    constructor() {
        super(ComponentKindMap);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: MapComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const address = makeText(builder, desc.location.addressPropertyName, tables.input);
        if (address === undefined) return b.emptyUI;

        const onTap = builder.makeSequenceFromActions(
            this.getActions(desc, tables.input, builder, mutatingScreenKind).actions,
            tables,
            undefined,
            outputContext,
            mutatingScreenKind,
            false
        );

        return b.ui("Map", {
            address,
            zoomLevel: b.constant(getZoomLevelNumber(getEnumProperty(desc.zoomLevel) as MapZoomLevel)),
            onTap,
        });
    }

    protected makeEmptyDescription(): MapComponentDescription {
        return {
            ...super.makeEmptyDescription(),
            location: { kind: MapLocationKind.Address },
        } as MapComponentDescription;
    }

    public getDescriptor(
        _desc: MapComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<MapComponentDescription> {
        return {
            name: "Map",
            description: "A map that opens full screen when tapped",
            img: "componentMap",
            group: "Media",
            helpUrl: getDocURL("mapImage"),
            properties: [
                makeTextPropertyDescriptor<MapComponentDescription>(
                    {
                        id: "address",
                        get: desc => desc.location.addressPropertyName,
                        update: (desc, newColumnName) => ({
                            location: { ...desc.location, addressPropertyName: defined(newColumnName) },
                        }),
                    },
                    "Address",
                    "Enter address",
                    true,
                    mutatingScreenKind,
                    {
                        getAllowedColumns: t => t.columns.filter(c => isStringTypeKind(c.type.kind)),
                        preferredNames: ["address", "location"],
                        columnFirst: true,
                    }
                ),
                {
                    kind: PropertyKind.Enum,
                    property: { name: "zoomLevel" },
                    label: "Zoom",
                    menuLabel: "Zoom level",
                    cases: mapZoomLevels,
                    defaultCaseValue: MapZoomLevel.Near,
                    section: PropertySection.Design,
                    visual: "dropdown",
                },
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }

    public getActionDescriptors(): readonly ActionPropertyDescriptor<MapComponentDescription>[] {
        return [makeDefaultActionDescriptorWithKinds([ActionKind.OpenMap], true)];
    }
}
